import React, { useEffect, useRef, useState } from 'react';
import useBundleTranslation from 'i18n';
import { Box, Stack, Typography } from '@mui/material';
import MITable from 'components/common/grid/MITable';
import { FormComponentBuilder } from 'components/common/form/layout';
import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import { getEditUrl } from 'tools/tools';

export interface ItemsReferencedThisGlossaryTermProps extends FormControlProps {
    watchRichTextFields: string[];
}

export default function ItemsReferencedThisGlossaryTerm({
    controlProps,
    elementProps,
}: FormElementControlPropsType<ItemsReferencedThisGlossaryTermProps>) {
    const fieldsToWatch = controlProps.watchRichTextFields.map((field) => field + '_association');

    const { t } = useBundleTranslation(['app/editor/glossary/items-referenced-this-glossary-term']);
    const [gridsData, setGridsData] = useState<any>({});
    const [remountKey, setRemountKey] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        let prevWidth = 0;
        const observer = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width ?? 0;
            if (prevWidth === 0 && width > 0) {
                setRemountKey((k) => k + 1);
            }
            prevWidth = width;
        });

        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const prepareGridData = (data: any[]) => {
        const items = (data ?? [])
            .filter((item) => item)
            .map((item) => JSON.parse(item))
            .filter((item) => item.length > 0)
            .flat();

        const resultObj: any = {};
        items.forEach((item) => {
            if (!resultObj?.[item.type]) {
                resultObj[item.type] = {};
            }
            if (!resultObj?.[item.type]?.[item.id]) {
                resultObj[item.type][item.id] = item;
            }
        });

        setGridsData(resultObj);
    };
    useEffect(() => {
        const subscription = controlProps.form.hookFormWatch((value, { name, type }) => {
            if (name && fieldsToWatch.includes(name)) {
                const values = controlProps.form.hookFormGetValues(fieldsToWatch);
                prepareGridData(values);
            }
        });
        return () => subscription?.unsubscribe();
    }, [controlProps.form.hookFormWatch]);

    return (
        <Box ref={containerRef} sx={{ width: '100%' }}>
            {Object.values(gridsData).length > 0 && (
                <Stack key={remountKey} spacing={2} sx={{ pt: 2 }}>
                    {gridsData.element && (
                        <Box>
                            <Typography variant="heading" sx={{ mb: 2 }}>
                                {t('grid_title', { items: t('grid_element.title_entity') })}
                            </Typography>
                            <MITable
                                autoHeight
                                gridName={'elements_referenced_this_glossary_term'}
                                massActionsConfig={{ enabled: false }}
                                data={Object.values(gridsData.element).map((item: any) => {
                                    return {
                                        name: {
                                            component: 'link',
                                            config: {
                                                text: item.name,
                                                href: getEditUrl(item.element_type) + item.id,
                                                newTab: true,
                                            },
                                        },
                                    };
                                })}
                                height={500}
                                columns={[
                                    {
                                        name: 'name',
                                        title: t('grid_column_name'),
                                        resizable: false,
                                    },
                                ]}
                            />
                        </Box>
                    )}
                    {gridsData.user_group && (
                        <Box>
                            <Typography variant="heading" sx={{ mb: 2 }}>
                                {t('grid_title', { items: t('grid_user_group.title_entity') })}
                            </Typography>
                            <MITable
                                autoHeight
                                gridName={'user_groups_referenced_this_glossary_term'}
                                massActionsConfig={{ enabled: false }}
                                data={Object.values(gridsData.user_group).map((item: any) => {
                                    return {
                                        name: {
                                            component: 'link',
                                            config: {
                                                text: item.name,
                                                href: `/editor/group/${item.id}`,
                                                newTab: true,
                                            },
                                        },
                                    };
                                })}
                                height={500}
                                columns={[
                                    {
                                        name: 'name',
                                        title: t('grid_column_name'),
                                        resizable: false,
                                    },
                                ]}
                            />
                        </Box>
                    )}
                    {gridsData.glossary_term && (
                        <Box>
                            <Typography variant="heading" sx={{ mb: 2 }}>
                                {t('grid_title', { items: t('grid_glossary_term.title_entity') })}
                            </Typography>
                            <MITable
                                autoHeight
                                gridName={'glossary_terms_referenced_this_glossary_term'}
                                massActionsConfig={{ enabled: false }}
                                data={Object.values(gridsData.glossary_term).map((item: any) => {
                                    return {
                                        term: {
                                            component: 'link',
                                            config: {
                                                text: item.name,
                                                href: `/editor/glossary/${item.id}`,
                                                newTab: true,
                                            },
                                        },
                                        section: {
                                            component: 'link',
                                            config: {
                                                text: item.section,
                                                href: `/editor/glossary/section/${item.section_id}`,
                                                newTab: true,
                                                icon: item.section_icon,
                                            },
                                        },
                                    };
                                })}
                                height={500}
                                columns={[
                                    {
                                        name: 'term',
                                        title: t('grid_glossary_term.column_term'),
                                        resizable: false,
                                    },
                                    {
                                        name: 'section',
                                        title: t('grid_glossary_term.column_section'),
                                        resizable: false,
                                    },
                                ]}
                            />
                        </Box>
                    )}
                </Stack>
            )}
        </Box>
    );
}

export class ItemsReferencedThisGlossaryTermBuilder extends FormComponentBuilder {
    prepareProps(): ItemsReferencedThisGlossaryTermProps {
        return {
            ...this.controlProps,
            watchRichTextFields: this.elementProps.componentProps?.watchRichTextFields ?? [],
        };
    }
}
